import { ApolloClient, ApolloProvider, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { createBreakpoints } from '@chakra-ui/theme-tools';
import 'dotenv/config';
import React from 'react';
import ReactDOM from 'react-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import App from './App';
import './index.css';
import reportWebVitals from './reportWebVitals';

const { NODE_ENV } = process.env;
let BASE_URL = `${process.env.REACT_APP_API_PROD_URL}`;

if (NODE_ENV.toLowerCase() === 'staging') {
    BASE_URL = `${process.env.REACT_APP_API_STAGING_URL}`;
} else if (NODE_ENV.toLowerCase() === 'production') {
    BASE_URL = `${process.env.REACT_APP_API_PROD_URL}`;
} else {
    BASE_URL = `${process.env.REACT_APP_API_DEV_URL}`;
}

const httpLink = createHttpLink({
    uri: `${BASE_URL}/graphql`,
    cache: new InMemoryCache()
});

const authLink = setContext((_, { headers }) => {
    // get the authentication token from session storage if it exists
    const token = sessionStorage.getItem('accessToken');
    // return the headers to the context so httpLink can read them
    return {
        headers: {
            ...headers,
            authorization: token ? `bearer ${token}` : ''
        }
    };
});

export const client = new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache()
});

const queryClient = new QueryClient();
const HeadingStyle = {
    // style object for base or default style
    baseStyle: {
        color: 'midnight.500'
    },
    // styles for different sizes ("sm", "md", "lg")
    sizes: {
        lg: {
            fontSize: '32px'
        },
        md: {
            fontSize: '28px'
        },
        sm: {
            fontSize: '18px'
        }
    },
    // styles for different visual variants ("outline", "solid")
    variants: {},
    // default values for `size` and `variant`
    defaultProps: {
        size: 'lg',
        variant: ''
    }
};
const theme = extendTheme({
    styles: {
        global: (props) => ({
            body: {
                bg: '#FCFCFD'
            }
        })
    },
    breakpoints: createBreakpoints({
        sm: '320px',
        md: '481px',
        lg: '769px',
        xl: '1200px'
    }),
    fonts: {
        heading: 'Work Sans',
        body: 'Work Sans'
    },
    colors: {
        brand: {
            5: '#FAFAFF',
            25: '#F3F3FC',
            50: '#E9E9FB',
            100: '#DADAFA',
            200: '#B5B5F5',
            300: '#9191F0',
            400: '#6C6CEB',
            500: '#4747E6',
            600: '#3939B8',
            700: '#2B2B8A'
        },
        midnight: {
            10: '#FCFCFD',
            15: '#FAFAFB',
            25: '#F4F4F6',
            50: '#EEEDF2',
            75: '#E0DFE7',
            100: '#CECCDA',
            200: '#9D99B4',
            300: '#6D678F',
            400: '#3C3469',
            500: '#0B0144',
            600: '#090136',
            700: '#030014'
        },
        red: {
            50: '#FB4B6E',
            100: '#FB4B6E',
            200: '#FB4B6E',
            300: '#FB4B6E',
            400: '#FB4B6E',
            500: '#FB4B6E'
        },
        neongreen: {
            500: '#0BE5A0',
            600: '#00D692'
        }
    },
    spacing: {
        space: {
            0.5: '0.125rem',
            1: '0.25rem',
            1.5: '0.375rem',
            2: '0.5rem',
            2.5: '0.625rem',
            3: '0.75rem',
            3.5: '0.875rem',
            4: '1rem',
            5: '1.25rem',
            6: '1.5rem',
            7: '1.75rem',
            8: '2rem',
            9: '2.25rem',
            10: '2.5rem',
            12: '3rem',
            14: '3.5rem',
            16: '4rem',
            20: '5rem',
            24: '6rem',
            28: '7rem',
            32: '8rem',
            36: '9rem',
            40: '10rem',
            44: '11rem',
            48: '12rem',
            52: '13rem',
            56: '14rem',
            60: '15rem',
            64: '16rem',
            72: '18rem',
            80: '20rem',
            96: '24rem'
        }
    },

    radii: {
        none: '0',
        sm: '0.125rem',
        base: '0.25rem',
        md: '0.375rem',
        lg: '0.5rem',
        xl: '0.75rem',
        '2xl': '1rem',
        '3xl': '1.5rem',
        full: '9999px'
    },

    components: {
        Button: {
            baseStyle: {
                fontWeight: '600',
                size: 'lg',
                borderRadius: '8px',
                padding: '6',
                _hover: {
                    textDecoration: 'none'
                }
            },
            variants: {
                primary: {
                    backgroundColor: 'brand.500',
                    width: '100%',
                    color: 'white',
                    _hover: {
                        backgroundColor: 'brand.700'
                    }
                },
                danger: {
                    backgroundColor: 'red.100',
                    _hover: {
                        backgroundColor: 'red.200',
                        color: 'red.500'
                    }
                }
            }
        },
        Link: {
            baseStyle: {
                color: 'brand.500',
                fontWeight: '600'
            },
            variants: {
                wrapper: {
                    color: 'inherit',
                    _hover: 'none'
                }
            }
        },
        Input: {
            parts: ['field', 'addon'],
            baseStyle: {
                field: {
                    bg: '#000'
                }
            },
            variants: {
                'with-shadow': {
                    bg: 'black',
                    boxShadow: '0 0 2px 2px #efdfde'
                }
            }
        },
        Modal: {
            baseStyle: {
                dialog: {
                    padding: { base: '5px', lg: '20px' },
                    borderRadius: '20px'
                },
                header: {
                    fontSize: '36px',
                    lineHeight: '36px',
                    fontWeight: '700',
                    textAlign: 'center',
                    color: 'midnight.500'
                },
                body: {
                    textAlign: 'center'
                }
            }
        },
        Heading: HeadingStyle
    }
});

ReactDOM.render(
    <React.StrictMode>
        <ChakraProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
                <ApolloProvider client={client}>
                    <App />
                </ApolloProvider>
            </QueryClientProvider>
        </ChakraProvider>
    </React.StrictMode>,
    document.getElementById('root')
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
